import { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DefaultOrganizationRolesMap from "../../globalStore/utils/DefaultOrganizationRolesMap";
import MaterialUiButton from "../../components/Buttons/MaterialUiButton/MaterialUiButton";
import SimpleTextField from "../../components/Forms/FieldTypes/TextField";
import StateSelect from "../../components/Selects/StateSelect";
import {
  createOrganization,
  editOrganization,
} from "../../utils/API/Organization/Organization";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DescriptionIcon from "@mui/icons-material/Description";

export default function EditOrganization(props) {
  const {
    isCreate,
    notificationModal,
    onHide,
    onSuccess,
    organization,
    readOnly,
  } = props;
  const theme = useTheme();
  const { palette = {} } = theme;
  const { primary = {}, success = {}, mode } = palette;
  const isDark = mode === "dark";
  const greenColor = success?.main || "#A0C46D";
  const navyColor = primary?.main || "#002843";

  const classes = {
    buttonContainer: {
      marginTop: '1rem',
      justifyContent: "space-between",
    },
    root: {
      flexGrow: 1,
      padding: theme.spacing(2),
    },
    sectionHeader: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      marginBottom: "16px",
      marginTop: "20px",
      paddingBottom: "10px",
      borderBottom: `2px solid ${isDark ? greenColor + "30" : navyColor + "15"}`,
    },
    sectionTitle: {
      fontFamily: '"Lato", sans-serif',
      fontWeight: 700,
      fontSize: "1rem",
      color: isDark ? greenColor : navyColor,
      letterSpacing: "-0.01em",
    },
    sectionIcon: {
      width: "32px",
      height: "32px",
      borderRadius: "8px",
      background: isDark 
        ? `linear-gradient(135deg, ${greenColor}20 0%, ${greenColor}10 100%)`
        : `linear-gradient(135deg, ${navyColor}15 0%, ${greenColor}10 100%)`,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: isDark ? greenColor : navyColor,
    },
  }
  const [modalState, setModalState] = useState({
    error: {
      city: false,
      name: false,
      state: false,
    },
    name: "",
    validated: false,
    ...organization,
  });
  const { address = {}, propertiesMap = {} } = modalState;
  const { address1 = "", city = "", state = "", postalCode = "" } = address;
  const { organizationDetails = "" } = propertiesMap;
  const { name } = modalState;

  function handleSubmit() {
    const { address, name, organizationId, propertiesMap = {} } = modalState;
    let body = {
      address,
      name,
      organizationId,
      propertiesMap,
      rolesMap: DefaultOrganizationRolesMap,
    };
    if (isCreate) {
      createOrganization(props, body).then((response) => {
        if (response.success) {
          let notificationModalText = isCreate
            ? "Organization Successfully Created"
            : "Location Successfully Updated";
          notificationModal(notificationModalText);
          onHide();
          onSuccess();
        } else notificationModal(`Error: ${response.error}`, true);
      });
    } else {
      editOrganization({ ...props, organizationId }, body).then((response) => {
        if (response.success) {
          let notificationModalText = isCreate
            ? "Organization Successfully Created"
            : "Location Successfully Updated";
          notificationModal(notificationModalText);
          onHide();
          onSuccess();
        } else notificationModal(`Error: ${response.error}`, true);
      });
    }
  }

  function validateFields() {
    let isValidated = true;
    const fieldsToValidate = [
      { id: "address1", value: address1 },
      { id: "city", value: city },
      { id: "name", value: name },
      { id: "postalCode", value: postalCode },
      { id: "state", value: state },
    ];

    // If something is empty, we flip isValidated to false
    fieldsToValidate.forEach((element) => {
      const { id, value = "" } = element;
      if (value?.length === 0) {
        isValidated = false;

        setModalState((prevState) => ({
          ...prevState,
          error: {
            ...prevState.error,
            [id]: true,
          },
        }));
      }
    });

    // If isValidated is never flipped, we move on
    if (isValidated) {
      handleSubmit();
    }
  }

  return (
    <Box sx={classes.root}>
      <Grid container spacing={2}>
        {/* Section: Organization Information */}
        <Grid size={12}>
          <Box sx={classes.sectionHeader}>
            <Box sx={classes.sectionIcon}>
              <BusinessIcon sx={{ fontSize: "18px" }} />
            </Box>
            <Typography sx={classes.sectionTitle}>
              Organization Information
            </Typography>
          </Box>
        </Grid>

        {/* Organization Name */}
        <Grid size={12}>
          <SimpleTextField
            error={modalState.error?.name}
            id="name"
            label="Organization Name"
            onChange={(event) => {
              setModalState((prevState) => ({
                ...prevState,
                error: { ...prevState.error, name: false },
                name: event.target.value,
              }));
            }}
            disabled={readOnly}
            required
            value={name}
          />
        </Grid>

        {/* Section: Address */}
        <Grid size={12}>
          <Box sx={classes.sectionHeader}>
            <Box sx={classes.sectionIcon}>
              <LocationOnIcon sx={{ fontSize: "18px" }} />
            </Box>
            <Typography sx={classes.sectionTitle}>
              Address
            </Typography>
          </Box>
        </Grid>

        {/* Address */}
        <Grid size={6}>
          <SimpleTextField
            error={modalState.error?.address1}
            id="address1"
            label="Address"
            onChange={(event) => {
              const newAddress = modalState.address || {};
              newAddress.address1 = event.target.value;
              setModalState((prevState) => ({
                ...prevState,
                address: newAddress,
              }));
            }}
            disabled={readOnly}
            required
            value={address1}
          />
        </Grid>
        {/* City */}
        <Grid size={6}>
          <SimpleTextField
            error={modalState.error?.city}
            id="city"
            label="City"
            onChange={(event) => {
              const newAddress = modalState.address || {};
              newAddress.city = event.target.value;
              setModalState((prevState) => ({
                ...prevState,
                error: { ...prevState.error, city: false },
                address: newAddress,
              }));
            }}
            disabled={readOnly}
            required
            value={city}
          />
        </Grid>
        {/* State */}
        <Grid size={6}>
          <StateSelect
            error={modalState.error?.state}
            onChange={(state) => {
              let newAddress = modalState.address || {};
              newAddress.state = state;
              setModalState((prevState) => ({
                ...prevState,
                error: { ...prevState.error, state: false },
                address: newAddress,
              }));
            }}
            readOnly={readOnly}
            required={true}
            state={state}
          />
        </Grid>
        {/* Zip Code */}
        <Grid size={6}>
          <SimpleTextField
            error={modalState.error?.postalCode}
            id="postalCode"
            label="Zip Code"
            onChange={(event) => {
              const newAddress = modalState.address || {};
              newAddress.postalCode = event.target.value;
              setModalState((prevState) => ({
                ...prevState,
                address: newAddress,
              }));
            }}
            disabled={readOnly}
            required
            value={postalCode}
          />
        </Grid>

        {/* Section: Additional Details */}
        <Grid size={12}>
          <Box sx={classes.sectionHeader}>
            <Box sx={classes.sectionIcon}>
              <DescriptionIcon sx={{ fontSize: "18px" }} />
            </Box>
            <Typography sx={classes.sectionTitle}>
              Additional Details
            </Typography>
          </Box>
        </Grid>

        {/* Organization Details */}
        <Grid size={12}>
          <SimpleTextField
            disabled={readOnly}
            id="organizationDetails"
            label="Organization Details"
            multiline
            onChange={(event) => {
              const newPropertiesMap = modalState.propertiesMap || {};
              newPropertiesMap.organizationDetails = event.target.value;
              setModalState((prevState) => ({
                ...prevState,
                propertiesMap: newPropertiesMap,
              }));
            }}
            rows={4}
            value={organizationDetails}
          />
        </Grid>

      </Grid>

      {/* Cancel and Submit Buttons */}
      {readOnly ? null : (
        <Grid container sx={classes.buttonContainer}>
          {/* Cancel Button */}
          <Grid size={4}>
            <MaterialUiButton
              color="cancel"
              fullWidth
              label="Cancel"
              onClick={() => {
                onHide();
              }}
              variant="outlined"
            />
          </Grid>

          {/* Submit Button */}
          <Grid size={4}>
            <MaterialUiButton
              color="submit"
              fullWidth
              label="Submit"
              onClick={() => {
                validateFields();
              }}
              variant="contained"
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
